import { IconButton } from "@/subframe/components/IconButton";
import { TextField } from "@/subframe/components/TextField";
import { Button } from "@/subframe/components/Button";
import { DropdownMenu } from "@/subframe/components/DropdownMenu";
import * as SubframeCore from "@subframe/core";
import { ChatList } from "@/subframe/components/ChatList";
import { ActionCard } from "@/subframe/components/ActionCard";
import Friend from "./Friend";
import ChatRoom from "./ChatRoom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addSelectedLabel,
  getConversation,
  getRecipients,
  newMessageByWebhook,
  newRecipientWithMessage,
  removeSelectedLabel,
  resetRecipients,
  setCurrentRecipient,
  updateMessageStatus,
  updateRecipient,
} from "../redux/reducers/messengerSlice";
import { AppDispatch, RootState } from "../redux/store";
import { Item } from "../redux/interfaces/reducersInterfaces";
import {
  fetchLabels,
  guestModeBannerMessage,
} from "../redux/reducers/authSlice";
import Settings from "./Settings";
import LabelCreation from "./subcomponents/LabelCreation";
import DeleteLabelPopup from "./subcomponents/DeleteLabelPopup";
import SwitchToAiAssistantPopup from "./subcomponents/SwitchToAiAssistantPopup";
import { io, Socket } from "socket.io-client";
import { Message } from "../Interfaces/messagesInterface";
import SwitchToHumanAgent from "./subcomponents/SwitchToHunamAgentPopup";

//swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import { Loader } from "@/subframe/components/Loader";

import ChatSendNewMessage from "./ChatSendNewMessage";
import { NotifyerHelpLinksBar } from "./NotifyerHelpLinksBar";
import {
  selectItemDetailsLoading,
  selectItemPhoneNumber,
} from "../redux/reducers/itemDetailsSlice";
import { normalizePhoneDigits } from "../Utils/normalizePhoneDigits";


function Messenger() {
  const { currentRecipient, selectedLabels } = useSelector(
    (state: RootState) => state.messenger
  );
  const { labels } = useSelector((state: RootState) => state.auth);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const dispatch = useDispatch<AppDispatch>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const socket = useRef<Socket | null>(null);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [isChatSendNewMessageOpen, setIsChatSendNewMessageOpen] = useState(false);
  const [isLabelCreationOpen, setIsLabelCreationOpen] = useState(false);
  const [labelId, setLabelId] = useState<number>();
  const [isLabelDeleteOpen, setIsLabelDeleteOpen] = useState(false);
  const [isSwitchOn, setIsSwitchOn] = useState<boolean>(
    currentRecipient?.is_ai_assistant ? true : false
  );
  const [isSwitchToAiAssistantPopupOpen, setIsSwitchToAiAssistantPopupOpen] =
    useState(false);
  const [isSwitchToHumanAgentPopupOpen, setIsSwitchToHumanAgentPopupOpen] =
    useState(false);
  // const [isSelectDateAndTimePopupOpen, setSelectDateAndTimePopupOpen] =
  //   useState(false);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  // Language/video section state
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'es' | 'pt' | 'fr'>('en');
  const videoUrls: Record<string, string> = {
    en: 'https://www.youtube.com/embed/zB3DtzEqrKQ',
    es: 'https://www.youtube.com/embed/euVoq7BK-LI',
    pt: 'https://www.youtube.com/embed/MO_G7iRVNJY',
    fr: 'https://www.youtube.com/embed/lVvQRENWkM8',
  };

  const { system_user_id, is_guest } = useSelector(
    (state: RootState) => state.auth
  );
  const itemPhoneNumber = useSelector(selectItemPhoneNumber);
  const itemDetailsLoading = useSelector(selectItemDetailsLoading);
  const itemPhoneAutoSearchDone = useRef(false);

  const settingsLocked = is_guest;

  useEffect(() => {
    if (system_user_id) {
      dispatch(fetchLabels());
    }
  }, [system_user_id, dispatch]);

  // On open: search by item phone from Redux; open matching chat or new-chat modal
  useEffect(() => {
    if (!system_user_id || itemPhoneAutoSearchDone.current) return;
    if (itemDetailsLoading) return;

    if (!itemPhoneNumber) {
      itemPhoneAutoSearchDone.current = true;
      dispatch(getRecipients({ page: 1 }));
      return;
    }

    const searchDigits = normalizePhoneDigits(itemPhoneNumber);
    if (!searchDigits) {
      itemPhoneAutoSearchDone.current = true;
      dispatch(getRecipients({ page: 1 }));
      return;
    }

    itemPhoneAutoSearchDone.current = true;
    setSearchTerm(searchDigits);

    void (async () => {
      const result = await dispatch(
        getRecipients({ page: 1, searchKeyword: searchDigits })
      );

      if (!getRecipients.fulfilled.match(result)) {
        setIsChatSendNewMessageOpen(true);
        return;
      }

      const match = result.payload.items.find((item: Item) => {
        const phoneDigits = normalizePhoneDigits(item.recipient.phone_number);
        const phoneStringDigits = normalizePhoneDigits(
          item.recipient.phone_number_string
        );
        return (
          phoneDigits === searchDigits || phoneStringDigits === searchDigits
        );
      });

      if (match) {
        dispatch(setCurrentRecipient(match.recipient));
      } else {
        setIsChatSendNewMessageOpen(true);
      }
    })();
  }, [system_user_id, itemPhoneNumber, itemDetailsLoading, dispatch]);

  const { recipients, loading, recipientsNextPage } = useSelector(
    (state: RootState) => state.messenger
  );
  const { messages } = useSelector((state: RootState) => state.messenger);

  //socket options
  const options = {
    rememberUpgrade: true,
    transports: ["websocket"],
    secure: true,
    rejectUnauthorized: false,
  };

  const currentRecipientRef = useRef(currentRecipient);
  const currentLabelRef = useRef(labels);

  useEffect(() => {
    currentRecipientRef.current = currentRecipient; // Update ref when currentRecipient changes
  }, [currentRecipient]);

  useEffect(() => {
    currentLabelRef.current = labels;
  }, [labels]);

  useEffect(() => {
    // Only connect if we have a system_user_id
    if (!system_user_id) return;



    socket.current = io("https://chat-notifyer-backend.onrender.com/", {
      ...options,
      auth: {
        user_id: system_user_id
      }
    });

    const handleNewMessage = (message: Message) => {

      const currentRecipient = currentRecipientRef.current;
      const currentLabels = currentLabelRef.current;
      dispatch(
        newMessageByWebhook({
          message,
          currentRecipient,
          currentLabels,
          system_user_id,
        })
      );
    };

    const handleNewRecipientWithMessage = (newRecipient: Item) => {
      const currentRecipient = currentRecipientRef.current;
      dispatch(newRecipientWithMessage({ newRecipient, currentRecipient }));
    };

    const handleConnect = () => {
    };

    const handleDisconnect = () => {
    };

    const handleConnectError = () => {
    };

    // Set up event listeners
    socket.current.on("connect", handleConnect);
    socket.current.on("disconnect", handleDisconnect);
    socket.current.on("connect_error", handleConnectError);
    socket.current.on("newMessage", handleNewMessage);
    socket.current.on("newRecipientWithMessage", handleNewRecipientWithMessage);

    // Join user room for real-time updates
    socket.current.emit("joinUserRoom", { user_id: system_user_id });

    // Cleanup function
    return () => {
      if (socket.current) {
        socket.current.off("connect", handleConnect);
        socket.current.off("disconnect", handleDisconnect);
        socket.current.off("connect_error", handleConnectError);
        socket.current.off("newMessage", handleNewMessage);
        socket.current.off("newRecipientWithMessage", handleNewRecipientWithMessage);
        socket.current.disconnect();
        socket.current = null;
      }
    };
  }, [system_user_id, dispatch]);

  useEffect(() => {
    if (currentRecipient?.phone_number && system_user_id) {
      dispatch(
        getConversation({
          perPage: 500,
          pageNumber: 1,
          offset: 0,
          phoneNumber: currentRecipient?.phone_number,
        })
      );
    }
  }, [currentRecipient?.phone_number, dispatch, system_user_id]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.items]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);

    // If search is cleared, fetch normal data immediately
    if (newSearchTerm === "") {
      dispatch(resetRecipients());
      dispatch(
        getRecipients({
          page: 1,
          selectedLabels:
            selectedLabels.length > 0 ? selectedLabels : undefined,
        })
      );
    }
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchTerm.trim() !== "") {
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
          selectedLabels:
            selectedLabels.length > 0 ? selectedLabels : undefined,
        })
      );
    }
  };

  //Load more recipients observer
  const observer = useRef<IntersectionObserver | null>(null);
  const lastRecipientRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && recipientsNextPage !== null) {
          dispatch(
            getRecipients({
              page: recipientsNextPage,
              searchKeyword: searchTerm,
              selectedLabels:
                selectedLabels.length > 0 ? selectedLabels : undefined,
            })
          );
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, recipientsNextPage, dispatch, searchTerm, selectedLabels]
  );

  //handle AI -> Human Agent only
  const handleAiToHuman = () => {
    if (isSwitchOn) {
      if (currentRecipient && currentRecipient.id) {
        const { id, ...rest } = currentRecipient;
        setIsSwitchOn(false);
        dispatch(updateRecipient({ id, ...rest, is_ai_assistant: false }));
        setIsSwitchToHumanAgentPopupOpen(true);
        setTimeout(() => {
          setIsSwitchToHumanAgentPopupOpen(false);
        }, 3000);
      }
    }
  };

  //handle human agent/ai switch
  const handleSwitchChange = () => {
    if (isSwitchOn) {
      // If switching from true → false, show SwitchToHumanAgentPopup for 3 sec
      if (currentRecipient && currentRecipient.id) {
        const { id, ...rest } = currentRecipient;
        setIsSwitchOn(false);
        dispatch(updateRecipient({ id, ...rest, is_ai_assistant: false }));
        setIsSwitchToHumanAgentPopupOpen(true);
        setTimeout(() => {
          setIsSwitchToHumanAgentPopupOpen(false);
        }, 3000);
      }
    } else {
      // If switching from false → true, open AiAssistantPopup
      setIsSwitchToAiAssistantPopupOpen(true);
    }
  };

  const confirmSwitchToAiAssistant = () => {
    if (currentRecipient && currentRecipient.id) {
      const { id, ...rest } = currentRecipient;
      setIsSwitchToAiAssistantPopupOpen(false);
      setIsSwitchOn(true);
      dispatch(updateRecipient({ id, ...rest, is_ai_assistant: true }));
    }
  };

  const cancelSwitchToAiAssistant = () => {
    setIsSwitchToAiAssistantPopupOpen(false);
    setIsSwitchOn(false);
  };

  useEffect(() => {
    if (currentRecipient) {
      setIsSwitchOn(currentRecipient.is_ai_assistant);
    }
  }, [currentRecipient, currentRecipient?.is_ai_assistant]);

  //handle recipient last message status change
  const handleUpdateMessageStatus = (message: Message) => {
    if (message.recipient !== "" && message.status === "unread") {
      dispatch(
        updateMessageStatus({
          phone_number: message.phone_number,
          message_id: message.message_id,
          status: "read",
        })
      );
    }
  };

  //handle label slider
  const swiperRef = useRef<SwiperType>();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const updateArrows = () => {
    if (swiperRef.current) {
      const swiper = swiperRef.current;
      const isStart = swiper.isBeginning;
      const isEnd = swiper.isEnd;

      setShowLeftArrow(!isStart);
      setShowRightArrow(!isEnd);
    }
  };

  // Update arrows when labels change
  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
      requestAnimationFrame(() => {
        updateArrows();
      });
    }
  }, [selectedLabels]);

  // Handle label selection from dropdown
  const handleLabelSelect = (label: string) => {
    dispatch(addSelectedLabel(label));
    dispatch(
      getRecipients({
        page: 1,
        searchKeyword: searchTerm,
        selectedLabels: [...selectedLabels, label],
      })
    );
  };

  // Handle label removal from slider
  const handleLabelRemove = (label: string) => {
    dispatch(removeSelectedLabel(label));
    dispatch(removeSelectedLabel(label));
    const updatedLabels = selectedLabels.filter((l) => l !== label);

    // When removing last label, fetch normal data
    if (updatedLabels.length === 0) {
      dispatch(resetRecipients());
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
        })
      );
    } else {
      // Otherwise fetch with remaining labels
      dispatch(
        getRecipients({
          page: 1,
          searchKeyword: searchTerm,
          selectedLabels: updatedLabels,
        })
      );
    }
  };


  return (
    <div className="flex h-full min-h-0 w-full flex-col items-stretch bg-default-background">
      <NotifyerHelpLinksBar />
      <div className="flex min-h-0 flex-1 w-full flex-row items-start">
        <div className="flex max-w-[384px] grow shrink-0 basis-0 flex-col items-start self-stretch border-r border-solid border-neutral-border">
          {/* Left top */}
          <div className="flex w-full flex-col items-center gap-2 px-4 pt-4 pb-2 bg-gradient-to-b from-white to-gray-50/30 border-b border-gray-100 shadow-sm">
            <div className="flex w-full flex-col items-center gap-1 px-2 py-1">
              <div className="flex w-full items-center gap-2">
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  variant="filled"
                  label=""
                  helpText=""
                  icon="FeatherSearch"
                >
                  <TextField.Input
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchSubmit}
                    className="rounded-lg"
                  />
                </TextField>
                <SubframeCore.DropdownMenu.Root>
                  <SubframeCore.DropdownMenu.Trigger asChild={true}>
                    <Button
                      variant="neutral-secondary"
                      icon="FeatherFilter"
                      className="hover:bg-gray-100 transition-all duration-200 rounded-lg"
                    // onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                    >
                      Filter
                    </Button>
                  </SubframeCore.DropdownMenu.Trigger>
                  <SubframeCore.DropdownMenu.Portal>
                    <SubframeCore.DropdownMenu.Content
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      asChild={true}
                    >
                      <DropdownMenu>
                        {labels.length > 0 ? (
                          labels.map((labelItem, index) => (
                            <DropdownMenu.DropdownItem
                              key={index}
                              icon={null}
                              className={`${selectedLabels.includes(labelItem.label)
                                ? "pointer-events-none bg-gray-100"
                                : ""
                                }`}
                              onClick={() => handleLabelSelect(labelItem.label)}
                            >
                              <div className="flex items-center justify-between w-full">
                                <span>{labelItem.label}</span>
                                {selectedLabels.includes(labelItem.label) && (
                                  <span className="text-gray-500 text-sm">
                                    Selected
                                  </span>
                                )}
                              </div>
                            </DropdownMenu.DropdownItem>
                          ))
                        ) : (
                          <DropdownMenu.DropdownItem
                            icon={null}
                            disabled={true}
                            className="text-neutral-500"
                          >
                            No labels available
                          </DropdownMenu.DropdownItem>
                        )}
                      </DropdownMenu>
                    </SubframeCore.DropdownMenu.Content>
                  </SubframeCore.DropdownMenu.Portal>
                </SubframeCore.DropdownMenu.Root>
              </div>
              {selectedLabels?.length > 0 && (
                <div className="flex w-full items-center justify-center gap-1 pt-1.5 z-0">
                  <IconButton
                    className={`h-6 w-5 flex-none ${showLeftArrow
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                      } transition-all duration-200`}
                    size="small"
                    icon="FeatherChevronLeft"
                    onClick={() => swiperRef.current?.slidePrev()}
                  />

                  <Swiper
                    modules={[Navigation]}
                    onBeforeInit={(swiper) => {
                      swiperRef.current = swiper;
                    }}
                    onSlideChange={updateArrows}
                    onAfterInit={updateArrows}
                    onResize={updateArrows}
                    slidesPerView="auto"
                    spaceBetween={8}
                    className="max-w-[288px] grow shrink-0 basis-0"
                    observer={true}
                    observeParents={true}
                  >
                    {selectedLabels.map((label, index) => (
                      <SwiperSlide key={index} className="!w-auto">
                        <div className="flex items-center gap-0.5 rounded-full border border-solid border-blue-200 bg-blue-50 pl-1 pr-1 py-0.5 shadow-sm hover:shadow-md transition-all duration-200">
                          <span className="whitespace-nowrap text-caption font-caption text-blue-700 font-medium">
                            {label}
                          </span>
                          <IconButton
                            size="small"
                            icon="FeatherX"
                            onClick={() => handleLabelRemove(label)}
                            className="hover:bg-blue-100 rounded-full transition-colors duration-200"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  <IconButton
                    className={`h-6 w-5 flex-none ${showRightArrow
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none"
                      } transition-all duration-200`}
                    size="small"
                    icon="FeatherChevronRight"
                    onClick={() => swiperRef.current?.slideNext()}
                  />
                </div>
              )}
              <div className="flex w-full items-start pt-2">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setFilter("all")}
                    className={`px-2 py-2 text-xs rounded-md transition-all duration-200 font-medium ${
                      filter === "all" 
                        ? "bg-blue-500 text-white" 
                        : "bg-gray-50 text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilter("unread")}
                    className={`px-2 py-2 text-xs rounded-md transition-all duration-200 font-medium ${
                      filter === "unread" 
                        ? "bg-blue-500 text-white" 
                        : "bg-gray-50 text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                    }`}
                  >
                    Unread
                  </button>
                </div>
                <div className="flex w-full items-center justify-end gap-2 px-2">
                <IconButton
                  variant="brand-tertiary"
                  size="large"
                  icon="FeatherMessageSquarePlus"
                  onClick={() => setIsChatSendNewMessageOpen(true)}
                  className="hover:shadow-md transition-all duration-200 -mt-0.5"
                />
                <IconButton
                  variant="brand-tertiary"
                  size="large"
                  icon="FeatherSettings"
                  disabled={settingsLocked}
                  title={
                    settingsLocked
                      ? "Guests can't open workspace settings (labels). Ask a member or admin."
                      : "Workspace settings"
                  }
                  onClick={() => {
                    if (!settingsLocked) setIsSettingOpen(!isSettingOpen);
                  }}
                  className="hover:shadow-md transition-all duration-200 -mt-0.5"
                />
              </div>

              </div>
              {/* <div className="flex w-full items-center justify-end gap-2 px-2">
                <IconButton
                  variant="brand-tertiary"
                  size="large"
                  icon="FeatherMessageSquarePlus"
                  onClick={() => setIsChatSendNewMessageOpen(true)}
                  className="hover:shadow-md transition-all duration-200"
                />
                <IconButton
                  variant="brand-tertiary"
                  size="large"
                  icon="FeatherSettings"
                  onClick={() => setIsSettingOpen(!isSettingOpen)}
                  className="hover:shadow-md transition-all duration-200"
                />
              </div> */}
            </div>
          </div>
          {/* Left Bottom */}
          <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-auto custom-scrollbar relative bg-default-background">
            {is_guest ? (
              <div
                className="w-full shrink-0 border-b border-solid border-amber-200/80 bg-amber-50 px-3 py-2 text-caption font-caption text-amber-950"
                role="status"
              >
                {guestModeBannerMessage()}
              </div>
            ) : null}
            {/* Chat List UI */}
            <ChatList>
              {recipients && recipients.items && recipients.items.length > 0 ? (
                [...recipients.items]
                  .filter((item) => {
                    if (filter === "unread") {
                      return (
                        item.conversation?.recipient !== "" &&
                        item.conversation?.status === "unread"
                      );
                    }
                    return true; // Show all if filter is "all"
                  })
                  .sort((a, b) => {
                    const timeA = a.recipient.last_message_time
                      ? new Date(a.recipient.last_message_time).getTime()
                      : 0;

                    const timeB = b.recipient.last_message_time
                      ? new Date(b.recipient.last_message_time).getTime()
                      : 0;

                    return timeB - timeA; // Sort in descending order (latest first)
                  })
                  .map((item: Item, index: number) => {
                    const isActive = currentRecipient?.id === item.recipient.id;
                    
                    return (
                      <div
                        key={`${item.recipient.id}-${index}`}
                        ref={
                          index === recipients.items.length - 1
                            ? lastRecipientRef
                            : null
                        }
                        className={`flex w-full items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors border-b border-solid border-neutral-border ${
                          isActive
                            ? "bg-brand-600/18 border-l-4 border-l-brand-600 pl-[calc(1rem-3px)] shadow-[inset_0_0_0_1px_rgba(2,132,199,0.25)]"
                            : "border-l-4 border-l-transparent hover:bg-neutral-100"
                        }`}
                        onClick={() => {
                          dispatch(setCurrentRecipient(item.recipient));
                          handleUpdateMessageStatus(item.conversation);
                        }}
                      >
                        <Friend
                          recipient={item.recipient}
                          lastMessage={item.conversation}
                        />
                      </div>
                    );
                  })
              ) : loading ? (
                <div className="flex items-center justify-center w-full">
                  <Loader size="medium" />
                </div>
              ) : (
                <div className="flex w-full items-center justify-center px-3 py-3">
                  <span className="text-body font-body text-neutral-700">
                    No chat available
                  </span>
                </div>
              )}
            </ChatList>
            
            {/* Settings Modal - Rendered at root level */}
            <Settings
              setIsSettingOpen={setIsSettingOpen}
              isSettingOpen={isSettingOpen}
              setIsLabelCreationOpen={setIsLabelCreationOpen}
              setIsLabelDeleteOpen={setIsLabelDeleteOpen}
              setLabelId={setLabelId}
            />
            
            {/* Old Settings Slide-in Container - Keep for backward compatibility */}
            <div
              className={`absolute top-0 left-0 flex max-w-[384px] h-full bg-default-background grow shrink-0 basis-0 flex-col items-start gap-4 py-4 overflow-auto custom-scrollbar
                     transition-transform duration-300 ease-in-out
                     ${isSettingOpen ? "translate-x-0" : "-translate-x-full"}`}
            >
              {/* This container is now empty since Settings is rendered as modal above */}
            </div>
            <LabelCreation
              open={isLabelCreationOpen}
              setIsLabelCreationOpen={setIsLabelCreationOpen}
            />
            <DeleteLabelPopup
              open={isLabelDeleteOpen}
              setIsLabelDeleteOpen={setIsLabelDeleteOpen}
              labelId={labelId}
            />
            <SwitchToAiAssistantPopup
              open={isSwitchToAiAssistantPopupOpen}
              setIsSwitchToAiAssistantPopupOpen={
                setIsSwitchToAiAssistantPopupOpen
              }
              onConfirm={confirmSwitchToAiAssistant}
              onCancel={cancelSwitchToAiAssistant}
            />
            <SwitchToHumanAgent
              open={isSwitchToHumanAgentPopupOpen}
              setIsSwitchToHumanAgentPopupOpen={
                setIsSwitchToHumanAgentPopupOpen
              }
            />
            <ChatSendNewMessage
              isNewChatOpen={isChatSendNewMessageOpen}
              setIsNewChatOpen={setIsChatSendNewMessageOpen}
            />
            {/* <SelectDateAndTimePopup
                open={isSelectDateAndTimePopupOpen}
                setSelectDateAndTimePopupOpen={setSelectDateAndTimePopupOpen}
              /> */}

            <div className="hidden w-full grow shrink-0 basis-0 flex-col items-center justify-end gap-4">
              <ActionCard
                className="h-auto w-full flex-none"
                icon="FeatherSettings2"
                title="Chat Setting"
                desc="Usability tests, surveys, card sort, and more"
              />
            </div>
          </div>

          {/* Floating Button - Outside scrollable container but inside Chats section */}
          <div className="absolute bottom-4 left-4 z-0">
            <IconButton
              variant="brand-primary"
              size="large"
              icon="FeatherMessageSquarePlus"
              onClick={() => setIsChatSendNewMessageOpen(true)}
              className="rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out hover:scale-110 active:scale-95 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500"
            />
          </div>

        </div>
        {/* Middle part- chat room */}
        {currentRecipient && currentRecipient.phone_number ? (
          <ChatRoom
            currentRecipient={currentRecipient}
            messages={messages}
            scrollRef={scrollRef}
            // setSelectDateAndTimePopupOpen={setSelectDateAndTimePopupOpen}
            isSwitchOn={isSwitchOn}
            onToggle={handleSwitchChange}
            handleAiToHuman={handleAiToHuman}
          />
        ) : (
          <div className="flex grow shrink-0 basis-0 flex-col items-center justify-center self-stretch border-l border-solid border-neutral-border overflow-y-auto">
            <div className="flex flex-col items-center gap-6 max-w-4xl mx-auto px-4 py-8">
              {/* Three Logos Section */}
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-4">
                  {/* Monday Logo */}
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-xl shadow-md p-3">
                    <img 
                      src="/monday-200x200-1.png" 
                      alt="Monday.com Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  {/* Plus Icon */}
                  <div className="text-gray-400 text-xl font-light">+</div>
                  
                  {/* Notifyer Logo */}
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-xl shadow-md p-3">
                    <img 
                      src="/notifyer.svg" 
                      alt="Notifyer Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  
                  {/* Plus Icon */}
                  <div className="text-gray-400 text-xl font-light">+</div>
                  
                  {/* WhatsAble App Logo */}
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-xl shadow-md p-3">
                    <img 
                      src="/whatsable-logo-transparent.svg" 
                      alt="WhatsAble Logo" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                
                {/* Tagline */}
                <h1 className="text-lg font-semibold text-gray-800 text-center">
                  Notifyer By WhatsAble Chat
                </h1>
                <p className="text-xs text-gray-600 text-center leading-relaxed max-w-md">
                  Chat seamlessly with recipients, manage message templates, and organize conversations with custom labels - plus much more
                </p>
              </div>

              {/* Language/Video Section */}
              <div className="flex w-full flex-col items-center gap-2 mt-1">
                <div className="flex w-full items-center justify-center gap-2">
                  <button
                    onClick={() => setSelectedLanguage('en')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedLanguage === 'en' 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => setSelectedLanguage('es')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedLanguage === 'es' 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    Español
                  </button>
                  <button
                    onClick={() => setSelectedLanguage('pt')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedLanguage === 'pt' 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    Portuguese
                  </button>
                  <button
                    onClick={() => setSelectedLanguage('fr')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                      selectedLanguage === 'fr' 
                        ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm'
                    }`}
                  >
                    French
                  </button>
                </div>
                <div className="flex items-center justify-center overflow-hidden w-full max-w-sm aspect-video bg-black relative rounded-lg shadow-md">
                  <iframe
                    key={selectedLanguage}
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                    src={videoUrls[selectedLanguage]}
                    title="Language Demo Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>

              {/* Key Features Section */}
              <div className="flex flex-col items-center gap-2 w-full mt-3">
                <h2 className="text-sm font-semibold text-gray-900 tracking-wide uppercase">Key Features</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 w-full max-w-2xl">
                  {/* Advanced Scheduling */}
                  <div className="flex flex-row items-center gap-2.5 px-2.5 py-2 rounded-md border border-gray-100 bg-white hover:shadow-sm transition-shadow">
                    <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-xs font-semibold text-gray-900">Advanced Scheduling</h3>
                      <p className="text-[11px] text-gray-600 leading-snug">Schedule messages with intelligent delay options</p>
                    </div>
                  </div>

                  {/* AI Automation */}
                  <div className="flex flex-row items-center gap-2.5 px-2.5 py-2 rounded-md border border-gray-100 bg-white hover:shadow-sm transition-shadow">
                    <div className="w-7 h-7 rounded-md bg-purple-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-xs font-semibold text-gray-900">AI Automation</h3>
                      <p className="text-[11px] text-gray-600 leading-snug">Smart automation with human touchpoints</p>
                    </div>
                  </div>

                  {/* Team Management */}
                  <div className="flex flex-row items-center gap-2.5 px-2.5 py-2 rounded-md border border-gray-100 bg-white hover:shadow-sm transition-shadow">
                    <div className="w-7 h-7 rounded-md bg-green-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-xs font-semibold text-gray-900">Team Management</h3>
                      <p className="text-[11px] text-gray-600 leading-snug">Granular controls and accountability tools</p>
                    </div>
                  </div>

                  {/* Smart Labels */}
                  <div className="flex flex-row items-center gap-2.5 px-2.5 py-2 rounded-md border border-gray-100 bg-white hover:shadow-sm transition-shadow">
                    <div className="w-7 h-7 rounded-md bg-orange-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-xs font-semibold text-gray-900">Smart Labels</h3>
                      <p className="text-[11px] text-gray-600 leading-snug">Intelligent labeling and filtering system</p>
                    </div>
                  </div>

                  {/* Message Templates */}
                  <div className="flex flex-row items-center gap-2.5 px-2.5 py-2 rounded-md border border-gray-100 bg-white hover:shadow-sm transition-shadow">
                    <div className="w-7 h-7 rounded-md bg-pink-100 flex items-center justify-center">
                      <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-xs font-semibold text-gray-900">Message Templates</h3>
                      <p className="text-[11px] text-gray-600 leading-snug">Customizable templates for efficiency</p>
                    </div>
                  </div>

                  {/* Real-time CRM Sync */}
                  <div className="flex flex-row items-center gap-2.5 px-2.5 py-2 rounded-md border border-gray-100 bg-white hover:shadow-sm transition-shadow">
                    <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justifyCenter">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <h3 className="text-xs font-semibold text-gray-900">Real-time CRM Sync</h3>
                      <p className="text-[11px] text-gray-600 leading-snug">Auto-sync: all messages to your CRM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Download App Banner */}
            <div className="mt-2 flex flex-col items-center gap-2 max-w-2xl mx-auto">
              <div className="flex gap-3">
                <a
                  href="https://apps.apple.com/es/app/notifyer-by-whatsable/id6743722183?l=en-GB"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="Download on the App Store"
                    className="h-10"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.chatmobile.chat_mobile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Get it on Google Play"
                    className="h-10"
                  />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Messenger;
